import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '@/App';
import { API } from '@/App';
import { Download, Users, Building2, Lock, Eye, EyeOff, Home, Mail, Loader2, Key, X, ShieldCheck, ShieldOff, AlertTriangle, User } from 'lucide-react';

// === AXIOS INTERCEPTOR (module-level) ===
// 401 yakalanirsa otomatik logout (admin token expired/invalid).
// Sayfa admin sayfasiysa reload — login formu gelir.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAdminEndpoint =
        url.includes('/api/admin') ||
        url.includes('/influencer-counts') ||
        url.includes('/influencer-signups') ||
        url.includes('/brand-contacts') ||
        url.includes('/export-');
      if (isAdminEndpoint) {
        localStorage.removeItem('admin_token');
        if (typeof window !== 'undefined' && window.location.pathname.includes('admin')) {
          window.location.reload();
        }
      }
    }
    return Promise.reject(error);
  }
);

// Helper: localStorage'dan token al, Authorization header objesi don.
const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AdminPage = () => {
  const { language } = useLanguage();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Auth state — backend JWT pattern (Commit 2/4)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [adminEmail, setAdminEmail] = useState(''); // Header'da gosterim icin

  // === SEKMELER ===
  // URL'e YAZILMAZ: App.js /adminboraorkun/* altini koke indirgiyor, yani alt rota
  // calismiyor. Sekme yenilemede sifirlanir — bilincli, mevcut rota davranisi bozulmasin.
  const [aktifSekme, setAktifSekme] = useState('genel');

  // === MDB WHITELIST ===
  const [mdbUsers, setMdbUsers] = useState([]);
  const [mdbLoading, setMdbLoading] = useState(false);
  const [mdbError, setMdbError] = useState('');
  const [mdbBusy, setMdbBusy] = useState('');           // islem suren e-posta
  const [mdbYeniEmail, setMdbYeniEmail] = useState('');
  const [mdbYeniRol, setMdbYeniRol] = useState('team');
  // Sifre YALNIZCA formdan gelir — kodda sabit yok, sunucu da uretmiyor.
  // Basarili istekten sonra state temizlenir; hicbir yerde saklanmaz, loglanmaz.
  const [mdbYeniSifre, setMdbYeniSifre] = useState('');
  // Sifre sifirlama satir ici form: hangi satir acik + o satirin sifre alani.
  const [sifirlaEmail, setSifirlaEmail] = useState('');
  const [sifirlaSifre, setSifirlaSifre] = useState('');
  // Arsiv varsayilan KAPALI: liste uzuyordu ve kapali hesaplar aktiflerin arasinda
  // duruyordu. active === false olan hesap arsivdedir — ayri bir alan yok.
  const [arsivAcik, setArsivAcik] = useState(false);
  // Sifre goster/gizle — ikisi AYRI bayrak: ekleme formu ve satir ici sifirlama
  // formu ayni anda acik olabiliyor, tek bayrak ikisini birden acardi.
  const [mdbYeniSifreGoster, setMdbYeniSifreGoster] = useState(false);
  const [sifirlaSifreGoster, setSifirlaSifreGoster] = useState(false);
  // Silme onayi — hangi hesap icin acik. Tek tiklamayla silinmemesi icin.
  const [silinecek, setSilinecek] = useState(null);
  const [error, setError] = useState('');
  // Influencer sayimi — sadece cut-off sonrasi (yeni format) kayitlar.
  // Backend /api/influencer-counts endpoint'i { count } donduruyor.
  const [influencerCount, setInfluencerCount] = useState(0);
  const [brandCount, setBrandCount] = useState(0);
  // 41-sutunlu tablo state'i — /api/influencer-signups-formatted endpoint'inden gelir
  const [signupRows, setSignupRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);
  // 2FA state'leri (Commit 4/4 — auth hardening)
  // authStep: login -> 2fa-setup (ilk giris) veya 2fa-verify (sonraki girisler) -> tam token + panel
  const [authStep, setAuthStep] = useState('login');
  const [tempToken, setTempToken] = useState('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [totpError, setTotpError] = useState('');

  // Sifremi unuttum state'leri (Commit 6/6)
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');

  // Peer admin reset state'leri (Commit 7/7) — "Diger Admin Yonetimi" section + confirmation modal
  const [adminList, setAdminList] = useState([]);
  const [loadingAdminList, setLoadingAdminList] = useState(false);
  const [peerResetTarget, setPeerResetTarget] = useState(null); // null = modal kapali; string = hedef email
  const [peerResetLoading, setPeerResetLoading] = useState(false);
  const [peerResetError, setPeerResetError] = useState('');
  const [peerResetSuccess, setPeerResetSuccess] = useState(false);

  // Sifre degistirme modal state'leri (Commit 3/4)
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);

  // ADMIN_PASSWORD constant KALDIRILDI — backend JWT auth kullaniliyor (Commit 2/4)

  const content = {
    tr: {
      loginTitle: 'Admin Girişi',
      password: 'Şifre',
      login: 'Giriş Yap',
      wrongPassword: 'Yanlış şifre!',
      title: 'Admin Paneli',
      subtitle: 'Form verilerini Excel formatında indirin',
      influencers: 'Influencer Başvuruları',
      brands: 'Marka İletişim Formları',
      download: 'Excel İndir',
      total: 'Toplam',
      entries: 'kayıt',
      logout: 'Çıkış Yap',
      home: 'Ana Sayfa',
      // Auth — Commit 2/4
      emailPlaceholder: 'Email adresiniz',
      passwordPlaceholder: 'Şifreniz',
      loggingIn: 'Giriş yapılıyor...',
      rateLimitError: 'Çok fazla giriş denemesi. 15 dakika sonra tekrar deneyin.',
      loginError: 'Giriş yapılamadı. Lütfen tekrar deneyin.',
      downloadError: 'İndirme hatası. Tekrar deneyin.',
      tabGeneral: 'Genel', tabMdb: 'MDB Whitelist',
      mdbTitle: 'Master Database kullanicilari',
      mdbSubtitle: 'Bu listedeki hesaplar /master-database sayfasina erisebilir.',
      mdbColEmail: 'E-posta', mdbColRole: 'Rol', mdbColStatus: 'Durum',
      mdbCol2fa: '2FA', mdbColLastLogin: 'Son giris', mdbColActions: 'Islemler',
      mdbActive: 'Acik', mdbInactive: 'Kapali', mdb2faOn: 'Kurulu', mdb2faOff: 'Kurulu degil',
      mdbNever: 'Hic', mdbEmpty: 'Henuz kullanici yok.',
      mdbAdd: 'Kullanici ekle', mdbEmailPlaceholder: 'ad@personaajans.com',
      mdbResetPassword: 'Sifre sifirla', mdbReset2fa: '2FA sifirla',
      mdbDisable: 'Kapat', mdbEnable: 'Ac',
      mdbLocked: 'Sabit liste — buradan yonetilemez',
      mdbLoadError: 'Kullanici listesi alinamadi', mdbActionError: 'Islem basarisiz',
      mdbDomainError: 'Yalnizca {alanlar} adresleri eklenebilir',
      mdbArchive: 'Arşiv',
      mdbDelete: 'Sil',
      mdbDeleteTitle: 'Bu hesabi silmek istediginize emin misiniz?',
      mdbDeleteBody: 'Hesap kalici olarak silinecek. Bu kullanicinin master database\'de yaptigi degisikliklerin denetim kayitlari silinmez, ancak hesap listede gorunmeyecegi icin gecmiste kimin ne yaptigini izlemek zorlasir.',
      mdbDeleteAlt: 'Hesabi silmek yerine kapatabilirsiniz — erisimi kesilir, kayit arsivde kalir.',
      mdbDeleteCancel: 'Iptal', mdbDeleteConfirm: 'Evet, sil',
      mdbMustChange: 'sifre degistirmeli',
      mdbPassword: 'Gecici sifre', mdbPasswordHint: 'en az 12 karakter',
      mdbCancel: 'Vazgec',
      // Şifre değiştirme — Commit 3/4
      changePassword: 'Şifre Değiştir',
      currentPassword: 'Mevcut Şifre',
      newPassword: 'Yeni Şifre',
      confirmNewPassword: 'Yeni Şifre (Tekrar)',
      currentPasswordPlaceholder: 'Mevcut şifrenizi girin',
      newPasswordPlaceholder: 'Yeni şifrenizi girin',
      confirmNewPasswordPlaceholder: 'Yeni şifrenizi tekrar girin',
      passwordRules: 'Yeni şifre kuralları',
      passwordRule1: 'En az 12 karakter',
      passwordRule2: 'En az bir büyük harf (A-Z)',
      passwordRule3: 'En az bir küçük harf (a-z)',
      passwordRule4: 'En az bir rakam (0-9)',
      passwordRule5: 'En az bir özel karakter (!@#$% vb.)',
      passwordMismatch: 'Yeni şifreler eşleşmiyor',
      passwordTooShort: 'Yeni şifre en az 12 karakter olmalı',
      passwordWeak: 'Yeni şifre güvenlik kurallarını karşılamıyor',
      currentPasswordWrong: 'Mevcut şifre yanlış',
      changePasswordError: 'Şifre değiştirilemedi. Lütfen tekrar deneyin.',
      changingPassword: 'Değiştiriliyor...',
      changePasswordSubmit: 'Şifreyi Değiştir',
      passwordChangedSuccess: 'Şifre başarıyla değiştirildi',
      // 2FA — Commit 4/4
      totpSetupTitle: 'İki Faktörlü Doğrulama Kurulumu',
      totpSetupSubtitle: 'Hesabınızı korumak için Google Authenticator kurmanız gerekiyor',
      totpStep1: 'Telefonunuzda Google Authenticator uygulamasını açın',
      totpStep2: 'Aşağıdaki QR kodu uygulamanızla tarayın',
      totpStep3: 'Uygulamanın gösterdiği 6 haneli kodu girin',
      totpManualEntry: 'QR kod taranamıyor mu? Manuel kod',
      totpEnterCode: '6 Haneli Kod',
      totpVerifying: 'Doğrulanıyor...',
      totpVerifyAndContinue: 'Doğrula ve Devam Et',
      totpVerifyTitle: 'İki Faktörlü Doğrulama',
      totpVerifySubtitle: 'Google Authenticator uygulamanızdaki 6 haneli kodu girin',
      totpVerifyButton: 'Giriş Yap',
      totpBackToLogin: 'Giriş ekranına dön',
      totpInvalidFormat: '6 haneli sayı olmalı',
      totpCodeWrong: 'Kod yanlış. Tekrar deneyin.',
      totpVerifyError: 'Doğrulama başarısız. Tekrar deneyin.',
      totpSetupError: '2FA kurulumu başlatılamadı. Tekrar deneyin.',
      // Şifremi unuttum — Commit 6/6
      forgotPassword: 'Şifremi Unuttum?',
      forgotPasswordTitle: 'Şifre Sıfırlama',
      forgotPasswordDescription: 'Email adresinizi girin, şifre sıfırlama linki gönderilsin.',
      forgotEmailLabel: 'Email Adresi',
      forgotEmailPlaceholder: 'Email adresiniz',
      forgotEmailRequired: 'Email adresi gerekli',
      forgotSubmit: 'Sıfırlama Linki Gönder',
      forgotSending: 'Gönderiliyor...',
      forgotEmailSent: 'Eğer bu email kayıtlıysa, şifre sıfırlama linki gönderildi.',
      forgotCheckEmail: 'Email kutunuzu kontrol edin (spam dahil). Link 1 saat geçerlidir.',
      forgotError: 'Şifre sıfırlama talebi gönderilemedi. Tekrar deneyin.',
      forgotRateLimit: 'Çok fazla şifre sıfırlama talebi. 1 saat sonra tekrar deneyin.',
      close: 'Kapat',
      // Peer admin reset — Commit 7/7
      adminMgmtTitle: 'Diğer Admin Yönetimi',
      adminMgmtSubtitle: 'Telefon kaybı / 2FA sorunu yaşayan diğer admin için 2FA sıfırlayabilirsiniz',
      adminCardTwoFactorEnabled: '2FA Aktif',
      adminCardTwoFactorDisabled: '2FA Kurulmamış',
      adminCardLastLogin: 'Son giriş',
      adminCardLastLoginNever: 'Henüz giriş yapmadı',
      adminCardLastReset: 'Son 2FA Sıfırlama',
      adminCardYou: '(Siz)',
      peerResetButton: '2FA\'yı Sıfırla',
      peerResetModalTitle: '2FA Sıfırlama Onayı',
      peerResetWarning: 'DİKKAT: Bu işlem {email} adresinin 2FA kurulumunu sıfırlayacak. Hedef admin sonraki girişinde Google Authenticator\'ı yeniden kurmak zorunda kalacak.',
      peerResetConfirmation: 'Bu işlemi gerçekten yapmak istediğinize emin misiniz?',
      peerResetConfirmButton: 'Evet, Sıfırla',
      peerResetCancelButton: 'İptal',
      peerResetting: 'Sıfırlanıyor...',
      peerResetSuccessMsg: '2FA başarıyla sıfırlandı.',
      peerResetGenericError: '2FA sıfırlanamadı. Tekrar deneyin.',
      adminListLoading: 'Admin listesi yükleniyor...'
    },
    en: {
      loginTitle: 'Admin Login',
      password: 'Password',
      login: 'Login',
      wrongPassword: 'Wrong password!',
      title: 'Admin Panel',
      subtitle: 'Download form data in Excel format',
      influencers: 'Influencer Applications',
      brands: 'Brand Contact Forms',
      download: 'Download Excel',
      total: 'Total',
      entries: 'entries',
      logout: 'Logout',
      home: 'Home',
      // Auth — Commit 2/4
      emailPlaceholder: 'Your email address',
      passwordPlaceholder: 'Your password',
      loggingIn: 'Signing in...',
      rateLimitError: 'Too many login attempts. Try again in 15 minutes.',
      loginError: 'Login failed. Please try again.',
      downloadError: 'Download error. Try again.',
      tabGeneral: 'General', tabMdb: 'MDB Whitelist',
      mdbTitle: 'Master Database users',
      mdbSubtitle: 'Accounts listed here can access /master-database.',
      mdbColEmail: 'Email', mdbColRole: 'Role', mdbColStatus: 'Status',
      mdbCol2fa: '2FA', mdbColLastLogin: 'Last login', mdbColActions: 'Actions',
      mdbActive: 'Active', mdbInactive: 'Disabled', mdb2faOn: 'Enabled', mdb2faOff: 'Not set',
      mdbNever: 'Never', mdbEmpty: 'No users yet.',
      mdbAdd: 'Add user', mdbEmailPlaceholder: 'name@personaajans.com',
      mdbResetPassword: 'Reset password', mdbReset2fa: 'Reset 2FA',
      mdbDisable: 'Disable', mdbEnable: 'Enable',
      mdbLocked: 'Fixed list — not managed here',
      mdbLoadError: 'Could not load users', mdbActionError: 'Action failed',
      mdbDomainError: 'Only {alanlar} addresses can be added',
      mdbArchive: 'Archive',
      mdbDelete: 'Delete',
      mdbDeleteTitle: 'Are you sure you want to delete this account?',
      mdbDeleteBody: 'The account will be permanently deleted. Audit records of this user\'s changes in the master database are not deleted, but since the account will no longer appear in the list, tracing who did what in the past becomes harder.',
      mdbDeleteAlt: 'Instead of deleting, you can disable the account — access is revoked and the record stays in the archive.',
      mdbDeleteCancel: 'Cancel', mdbDeleteConfirm: 'Yes, delete',
      mdbMustChange: 'must change password',
      mdbPassword: 'Temporary password', mdbPasswordHint: 'at least 12 characters',
      mdbCancel: 'Cancel',
      // Change password — Commit 3/4
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmNewPassword: 'New Password (Confirm)',
      currentPasswordPlaceholder: 'Enter your current password',
      newPasswordPlaceholder: 'Enter your new password',
      confirmNewPasswordPlaceholder: 'Confirm your new password',
      passwordRules: 'New password rules',
      passwordRule1: 'At least 12 characters',
      passwordRule2: 'At least one uppercase letter (A-Z)',
      passwordRule3: 'At least one lowercase letter (a-z)',
      passwordRule4: 'At least one digit (0-9)',
      passwordRule5: 'At least one special character (!@#$% etc.)',
      passwordMismatch: 'New passwords do not match',
      passwordTooShort: 'New password must be at least 12 characters',
      passwordWeak: 'New password does not meet security rules',
      currentPasswordWrong: 'Current password is wrong',
      changePasswordError: 'Could not change password. Please try again.',
      changingPassword: 'Changing...',
      changePasswordSubmit: 'Change Password',
      passwordChangedSuccess: 'Password changed successfully',
      // 2FA — Commit 4/4
      totpSetupTitle: 'Two-Factor Authentication Setup',
      totpSetupSubtitle: 'You need to set up Google Authenticator to protect your account',
      totpStep1: 'Open the Google Authenticator app on your phone',
      totpStep2: 'Scan the QR code below with your app',
      totpStep3: 'Enter the 6-digit code shown in your app',
      totpManualEntry: "Can't scan QR? Manual code",
      totpEnterCode: '6-Digit Code',
      totpVerifying: 'Verifying...',
      totpVerifyAndContinue: 'Verify and Continue',
      totpVerifyTitle: 'Two-Factor Authentication',
      totpVerifySubtitle: 'Enter the 6-digit code from your Google Authenticator app',
      totpVerifyButton: 'Sign In',
      totpBackToLogin: 'Back to login',
      totpInvalidFormat: 'Must be 6 digits',
      totpCodeWrong: 'Wrong code. Try again.',
      totpVerifyError: 'Verification failed. Try again.',
      totpSetupError: 'Could not start 2FA setup. Try again.',
      // Forgot password — Commit 6/6
      forgotPassword: 'Forgot Password?',
      forgotPasswordTitle: 'Password Reset',
      forgotPasswordDescription: 'Enter your email address, a password reset link will be sent.',
      forgotEmailLabel: 'Email Address',
      forgotEmailPlaceholder: 'Your email address',
      forgotEmailRequired: 'Email address required',
      forgotSubmit: 'Send Reset Link',
      forgotSending: 'Sending...',
      forgotEmailSent: 'If this email is registered, a password reset link has been sent.',
      forgotCheckEmail: 'Check your inbox (including spam). Link is valid for 1 hour.',
      forgotError: 'Password reset request failed. Try again.',
      forgotRateLimit: 'Too many password reset attempts. Try again in 1 hour.',
      close: 'Close',
      // Peer admin reset — Commit 7/7
      adminMgmtTitle: 'Other Admin Management',
      adminMgmtSubtitle: 'You can reset 2FA for the other admin if they lose their phone',
      adminCardTwoFactorEnabled: '2FA Active',
      adminCardTwoFactorDisabled: '2FA Not Set Up',
      adminCardLastLogin: 'Last login',
      adminCardLastLoginNever: 'Never logged in',
      adminCardLastReset: 'Last 2FA Reset',
      adminCardYou: '(You)',
      peerResetButton: 'Reset 2FA',
      peerResetModalTitle: '2FA Reset Confirmation',
      peerResetWarning: 'WARNING: This will reset 2FA setup for {email}. The target admin will need to set up Google Authenticator again on their next login.',
      peerResetConfirmation: 'Are you sure you want to do this?',
      peerResetConfirmButton: 'Yes, Reset',
      peerResetCancelButton: 'Cancel',
      peerResetting: 'Resetting...',
      peerResetSuccessMsg: '2FA reset successfully.',
      peerResetGenericError: 'Could not reset 2FA. Try again.',
      adminListLoading: 'Loading admin list...'
    }
  };

  const t = content[language];

  // Mount'ta session restore — localStorage'da token varsa /api/admin-me ile validate et,
  // gecerliyse otomatik authenticated; gecersizse temizle + login formu goster.
  useEffect(() => {
    const restoreSession = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) return;

      try {
        const response = await axios.get(`${API}/admin-me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAdminEmail(response.data.email);
        setIsAuthenticated(true);
        fetchCounts();
        fetchSignups();
        fetchAdminList();
      } catch (err) {
        // Token invalid/expired — temizle (interceptor da yapar ama burada da garantile)
        localStorage.removeItem('admin_token');
      }
    };
    restoreSession();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/admin-login`, {
        email: email.trim().toLowerCase(),
        password,
      });

      // Backend artik temp_token doneriyor (10dk gecerli, sadece 2FA setup/verify icin).
      // Tam token 2FA verify sonrasi gelir; orada localStorage'a yazilir.
      const { temp_token, email: userEmail, requires_2fa_setup, requires_2fa_verify } = response.data;

      setTempToken(temp_token);
      setAdminEmail(userEmail);
      setPassword(''); // Sifre input'unu temizle (guvenlik)

      if (requires_2fa_setup) {
        // Ilk giris — 2FA kurulumu zorunlu, atlanmaz
        setAuthStep('2fa-setup');
        await initiate2FASetup(temp_token);
      } else if (requires_2fa_verify) {
        // 2FA aktif — sadece kod isteme ekranina yonlendir
        setAuthStep('2fa-verify');
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setError(t.rateLimitError);
      } else if (err.response?.status === 401) {
        setError(t.wrongPassword);
      } else {
        setError(t.loginError);
      }
      console.error('Login error:', err);
    } finally {
      setLoginLoading(false);
    }
  };

  // 2FA setup — POST /api/admin-2fa-setup ile QR kodu + secret cek
  const initiate2FASetup = async (token) => {
    try {
      const response = await axios.post(
        `${API}/admin-2fa-setup`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQrCodeDataUrl(response.data.qr_code);
      setTotpSecret(response.data.secret);
    } catch (err) {
      console.error('2FA setup error:', err);
      setTotpError(t.totpSetupError);
    }
  };

  // 2FA verify — 6 haneli kodu backend'e gonder, basariliysa tam token + admin paneli
  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setTotpLoading(true);
    setTotpError('');

    if (!/^\d{6}$/.test(totpCode)) {
      setTotpError(t.totpInvalidFormat);
      setTotpLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API}/admin-2fa-verify`,
        { code: totpCode },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );

      const { token } = response.data;

      // Tam token (24 saat, korumali endpoint'lere erisim)
      localStorage.setItem('admin_token', token);

      // 2FA state'lerini temizle, panel'e gec
      setTempToken('');
      setQrCodeDataUrl('');
      setTotpSecret('');
      setTotpCode('');
      setAuthStep('login');
      setIsAuthenticated(true);

      fetchCounts();
      fetchSignups();
      fetchAdminList();
    } catch (err) {
      if (err.response?.status === 401) {
        setTotpError(t.totpCodeWrong);
      } else {
        setTotpError(t.totpVerifyError);
      }
      setTotpCode(''); // Yanlis kodu temizle
    } finally {
      setTotpLoading(false);
    }
  };

  // 41-sutunlu tablo icin yeni format kayitlari + kolon tanimlarini ceker
  const fetchSignups = async () => {
    try {
      setLoadingTable(true);
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/influencer-signups-formatted`, { headers });
      setSignupRows(res.data.rows || []);
      setColumns(res.data.columns || []);
    } catch (error) {
      console.error('Failed to fetch signups:', error);
      // 401 ise interceptor zaten logout + reload yapar
    } finally {
      setLoadingTable(false);
    }
  };

  const fetchCounts = async () => {
    try {
      const headers = getAuthHeaders();
      // Influencer sayimi — sadece cut-off sonrasi (yeni format) kayitlar
      const [countsRes, brandsRes] = await Promise.all([
        axios.get(`${API}/influencer-counts`, { headers }),
        axios.get(`${API}/brand-contacts`, { headers }),
      ]);
      setInfluencerCount(countsRes.data.count || 0);
      setBrandCount(Array.isArray(brandsRes.data) ? brandsRes.data.length : 0);
    } catch (error) {
      console.error('Error fetching counts:', error);
      // 401 ise interceptor zaten logout + reload yapar
    }
  };

  // Excel indir — window.open yerine axios blob (Bearer token gonderebilmek icin).
  // 401 olursa interceptor logout yapar; diger hatalarda kullaniciya generic indirme hatasi.
  const handleDownload = async (type) => {
    try {
      const endpoint = type === 'influencers'
        ? `${API}/export-influencers`
        : `${API}/export-brand-contacts`;
      const filenamePrefix = type === 'influencers' ? 'persona-influencers' : 'persona-brand-contacts';

      const headers = getAuthHeaders();
      const response = await axios.get(endpoint, {
        headers,
        responseType: 'blob',
      });

      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filenamePrefix}-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setError(t.downloadError);
      setTimeout(() => setError(''), 3000);
    }
  };

  // Sifremi unuttum — Commit 6/6
  // Backend'den generic success doner (saldirgana hangi email kayitli oldugunu belli etmemek icin).
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError('');
    setForgotMessage('');

    if (!forgotEmail.trim()) {
      setForgotError(t.forgotEmailRequired);
      setForgotLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/admin-forgot-password`, {
        email: forgotEmail.trim().toLowerCase(),
      });
      setForgotMessage(response.data.message || t.forgotEmailSent);
      setForgotEmail('');
    } catch (err) {
      if (err.response?.status === 429) {
        setForgotError(err.response.data?.error || t.forgotRateLimit);
      } else {
        setForgotError(t.forgotError);
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotPasswordModal = () => {
    setShowForgotPasswordModal(false);
    setForgotEmail('');
    setForgotError('');
    setForgotMessage('');
  };

  // Forgot Password Modal JSX — variable'a alindi ki hem Login Screen hem Admin Panel
  // return'larinda render edilebilsin (Commit 6 hot fix). Eskiden sadece Admin Panel return'unda
  // vardi → Login Screen kullanicisi butona bassa bile modal acilmiyordu (modal bu return path'te yok).
  const forgotPasswordModalJsx = showForgotPasswordModal && (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={closeForgotPasswordModal}
    >
      <div
        className="bg-gray-900 border-2 border-[#FFDD59] rounded-xl shadow-2xl max-w-md w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={closeForgotPasswordModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition"
          aria-label="Kapat"
        >
          <X size={24} />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <Mail className="text-[#FFDD59]" size={28} />
          <h2 className="text-2xl font-bold text-white">{t.forgotPasswordTitle}</h2>
        </div>

        <p className="text-gray-400 text-sm mb-4">{t.forgotPasswordDescription}</p>

        {forgotMessage ? (
          <div className="p-4 bg-green-500/20 border border-green-500 text-green-300 rounded-lg text-center">
            <p className="font-semibold">✓ {forgotMessage}</p>
            <p className="text-xs text-gray-400 mt-2">{t.forgotCheckEmail}</p>
            <button
              onClick={closeForgotPasswordModal}
              className="mt-4 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition text-sm"
            >
              {t.close}
            </button>
          </div>
        ) : (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">{t.forgotEmailLabel}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder={t.forgotEmailPlaceholder}
                  required
                  autoComplete="email"
                  autoFocus
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#FFDD59]"
                  data-testid="forgot-email-input"
                />
              </div>
            </div>

            {forgotError && (
              <div className="p-3 bg-red-500/20 border border-red-500 text-red-300 rounded-lg text-sm">
                {forgotError}
              </div>
            )}

            <button
              type="submit"
              disabled={forgotLoading || !forgotEmail.trim()}
              className="w-full py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-500 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="forgot-submit"
            >
              {forgotLoading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  {t.forgotSending}
                </>
              ) : (
                t.forgotSubmit
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );

  // Peer admin reset — Commit 7/7
  const fetchAdminList = async () => {
    try {
      setLoadingAdminList(true);
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin-list`, { headers });
      setAdminList(response.data.admins || []);
    } catch (err) {
      console.error('Admin list fetch error:', err);
      // 401 ise interceptor zaten logout yapar
    } finally {
      setLoadingAdminList(false);
    }
  };

  const openPeerResetModal = (email) => {
    setPeerResetTarget(email);
    setPeerResetError('');
    setPeerResetSuccess(false);
  };

  const closePeerResetModal = () => {
    setPeerResetTarget(null);
    setPeerResetError('');
    setPeerResetSuccess(false);
  };

  const handlePeerReset = async () => {
    if (!peerResetTarget) return;
    setPeerResetLoading(true);
    setPeerResetError('');
    setPeerResetSuccess(false);

    try {
      const headers = getAuthHeaders();
      await axios.post(
        `${API}/admin-reset-other-2fa`,
        { target_email: peerResetTarget },
        { headers }
      );

      setPeerResetSuccess(true);
      // Liste yenilenir (totp_enabled false ve totp_reset_at gorunur)
      await fetchAdminList();
      // 3 saniye sonra modal kapanir
      setTimeout(() => {
        closePeerResetModal();
      }, 3000);
    } catch (err) {
      if (err.response?.data?.error) {
        setPeerResetError(err.response.data.error);
      } else {
        setPeerResetError(t.peerResetGenericError);
      }
    } finally {
      setPeerResetLoading(false);
    }
  };

  // Izinli alan adlari — sunucudaki IZINLI_EPOSTA_ALANLARI ile AYNI liste.
  // Bu kopya yalnizca ERKEN GERI BILDIRIM icin: kullanici formu gonderip 400
  // beklemesin. ESAS KONTROL SUNUCUDA; burasi atlansa bile uc reddeder.
  const IZINLI_EPOSTA_ALANLARI = ['personaajans.com'];
  const epostaAlaniIzinli = (email) =>
    IZINLI_EPOSTA_ALANLARI.includes(String(email || '').trim().toLowerCase().split('@')[1]);

  // --- MDB Whitelist islemleri ---
  // Token basligi icin dosyanin ORTAK getAuthHeaders'i kullanilir. Burada ayri bir
  // yardimci vardi ve anahtari 'adminToken' yaziyordu — dogrusu 'admin_token'.
  // Sonuc: istek `Bearer null` ile gidiyor, requireAdmin 401 donuyor ve dosyanin
  // ustundeki axios interceptor'i /api/admin gordugu icin GECERLI token'i siliyordu;
  // yani MDB sekmesine tiklayan kullanici disari atiliyordu.
  // Ayri bir kopya tutmak ayni hatanin tekrar edebilecegi bir yuzey birakiyor.

  const mdbYukle = async () => {
    setMdbLoading(true);
    setMdbError('');
    try {
      const res = await axios.get(`${API}/admin/mdb-users`, { headers: getAuthHeaders() });
      setMdbUsers(res.data.users || []);
    } catch (err) {
      setMdbError(err.response?.data?.error || t.mdbLoadError);
    } finally {
      setMdbLoading(false);
    }
  };

  // Sekme acilinca listeyi getir (her acilista taze — yetki ekrani bayat veri gostermemeli).
  // Bagimlilik listesi bilerek dar: mdbYukle her cizimde yeniden olusuyor, listeye
  // eklenirse effect surekli tetiklenir ve istek dongusu olusur.
  useEffect(() => {
    if (isAuthenticated && aktifSekme === 'mdb') mdbYukle();
  }, [isAuthenticated, aktifSekme]);

  const aktifKullanicilar = mdbUsers.filter((u) => u.active);
  const arsivKullanicilar = mdbUsers.filter((u) => !u.active);

  const mdbIslem = async (email, calis) => {
    setMdbBusy(email);
    setMdbError('');
    try {
      await calis();
      await mdbYukle();
    } catch (err) {
      setMdbError(err.response?.data?.error || t.mdbActionError);
    } finally {
      setMdbBusy('');
    }
  };

  // Tablo iki kez ciziliyor (aktif + arsiv), bu yuzden fonksiyona cikarildi.
  // Ayni satir davranisi — dugmeler dahil — her iki gorunumde de gecerli:
  // arsivdeki bir hesabin sifresini geri acmadan once sifirlamak isteyebiliriz.
  const mdbTablo = (liste, testId) => (
        <div className="overflow-x-auto border border-gray-800 rounded-xl">
          <table className="w-full text-sm" data-testid={testId}>
            <thead className="bg-gray-900 text-gray-400">
              <tr>
                <th className="text-left px-4 py-3">{t.mdbColEmail}</th>
                <th className="text-left px-4 py-3">{t.mdbColRole}</th>
                <th className="text-left px-4 py-3">{t.mdbColStatus}</th>
                <th className="text-left px-4 py-3">{t.mdbCol2fa}</th>
                <th className="text-left px-4 py-3">{t.mdbColLastLogin}</th>
                <th className="text-right px-4 py-3">{t.mdbColActions}</th>
              </tr>
            </thead>
            <tbody>
              {liste.map((u) => (
                <tr key={u.email} className="border-t border-gray-800" data-testid={`mdb-row-${u.email}`}>
                  <td className="px-4 py-3">
                    {u.email}
                    {u.must_change_password && (
                      <span className="ml-2 text-[11px] text-[#FFDD59]">({t.mdbMustChange})</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{u.role || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={u.active ? 'text-green-400' : 'text-red-400'}>
                      {u.active ? t.mdbActive : t.mdbInactive}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400">
                    {u.totp_enabled ? t.mdb2faOn : t.mdb2faOff}
                  </td>
                  <td className="px-4 py-3 text-gray-400">
                    {u.last_login ? new Date(u.last_login).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US') : t.mdbNever}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {/* Allowlist hesaplari sunucuda da reddediliyor; dugmeleri
                        basmak kullaniciya calisacakmis gibi gosterirdi. */}
                    {u.allowlist ? (
                      <span className="text-xs text-gray-500">{t.mdbLocked}</span>
                    ) : (
                      <div className="flex gap-2 justify-end flex-wrap">
                        <button
                          type="button"
                          disabled={mdbBusy === u.email}
                          onClick={() => {
                            // Satir ici form acilir; sifre KODDAN degil buradan gelir.
                            setSifirlaEmail(sifirlaEmail === u.email ? '' : u.email);
                            setSifirlaSifre('');
                            setSifirlaSifreGoster(false); // her acilista GIZLI baslar
                          }}
                          className="px-3 py-1.5 text-xs border border-gray-700 rounded-lg hover:bg-gray-800 transition disabled:opacity-50"
                          data-testid={`mdb-reset-password-${u.email}`}
                        >
                          {t.mdbResetPassword}
                        </button>
                        <button
                          type="button"
                          disabled={mdbBusy === u.email}
                          onClick={() => mdb2faSifirla(u.email)}
                          className="px-3 py-1.5 text-xs border border-gray-700 rounded-lg hover:bg-gray-800 transition disabled:opacity-50"
                          data-testid={`mdb-reset-2fa-${u.email}`}
                        >
                          {t.mdbReset2fa}
                        </button>
                        <button
                          type="button"
                          disabled={mdbBusy === u.email}
                          onClick={() => mdbDurumDegistir(u.email, !u.active)}
                          className={`px-3 py-1.5 text-xs rounded-lg transition disabled:opacity-50 ${
                            u.active
                              ? 'border border-red-800 text-red-300 hover:bg-red-900/30'
                              : 'border border-green-800 text-green-300 hover:bg-green-900/30'
                          }`}
                          data-testid={`mdb-toggle-${u.email}`}
                        >
                          {u.active ? t.mdbDisable : t.mdbEnable}
                        </button>
                        {/* Sil: tek tiklamayla SILMEZ, onay penceresi acar.
                            Arsivdeki satirlarda da gorunur — mdbTablo ikisinde de ayni. */}
                        <button
                          type="button"
                          disabled={mdbBusy === u.email}
                          onClick={() => setSilinecek(u.email)}
                          className="px-3 py-1.5 text-xs border border-red-800 text-red-300 rounded-lg hover:bg-red-900/30 transition disabled:opacity-50"
                          data-testid={`mdb-delete-${u.email}`}
                        >
                          {t.mdbDelete}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )).flatMap((satir, i) => {
                const u = liste[i];
                if (sifirlaEmail !== u.email) return [satir];
                return [
                  satir,
                  <tr key={`${u.email}-reset`} className="border-t border-gray-800 bg-gray-900/60">
                    <td colSpan={6} className="px-4 py-3">
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          mdbSifreSifirla(u.email);
                        }}
                        className="flex flex-wrap gap-2 items-center justify-end"
                        data-testid={`mdb-reset-form-${u.email}`}
                      >
                        <span className="text-xs text-gray-400 mr-auto">{u.email}</span>
                        <div className="relative">
                          <input
                            type={sifirlaSifreGoster ? 'text' : 'password'}
                            required
                            minLength={12}
                            value={sifirlaSifre}
                            onChange={(e) => setSifirlaSifre(e.target.value)}
                            placeholder={t.mdbPasswordHint}
                            className="pl-3 pr-9 py-1.5 text-sm bg-black border border-gray-700 rounded-lg text-white focus:border-[#FFDD59] outline-none"
                            data-testid={`mdb-reset-input-${u.email}`}
                          />
                          <button
                            type="button"
                            onClick={() => setSifirlaSifreGoster(!sifirlaSifreGoster)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                            data-testid={`mdb-reset-toggle-${u.email}`}
                          >
                            {sifirlaSifreGoster ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                        <button
                          type="submit"
                          disabled={mdbBusy === u.email}
                          className="px-4 py-1.5 text-xs bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition disabled:opacity-50"
                          data-testid={`mdb-reset-confirm-${u.email}`}
                        >
                          {t.mdbResetPassword}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setSifirlaEmail(''); setSifirlaSifre(''); }}
                          className="px-3 py-1.5 text-xs text-gray-400 hover:text-white transition"
                          data-testid={`mdb-reset-cancel-${u.email}`}
                        >
                          {t.mdbCancel}
                        </button>
                      </form>
                    </td>
                  </tr>,
                ];
              })}
            </tbody>
          </table>
        </div>
  );

  const mdbEkle = async (e) => {
    e.preventDefault();
    if (!epostaAlaniIzinli(mdbYeniEmail)) {
      setMdbError(t.mdbDomainError.replace('{alanlar}', IZINLI_EPOSTA_ALANLARI.map((a) => '@' + a).join(', ')));
      return;
    }
    setMdbBusy('__yeni__');
    setMdbError('');
    try {
      await axios.post(
        `${API}/admin/mdb-users`,
        { email: mdbYeniEmail, role: mdbYeniRol, password: mdbYeniSifre },
        { headers: getAuthHeaders() }
      );
      setMdbYeniEmail('');
      setMdbYeniRol('team');
      setMdbYeniSifre(''); // sifre state'te birakilmaz
      await mdbYukle();
    } catch (err) {
      setMdbError(err.response?.data?.error || t.mdbActionError);
    } finally {
      setMdbBusy('');
    }
  };

  const mdbSifreSifirla = (email) =>
    mdbIslem(email, async () => {
      await axios.post(
        `${API}/admin/mdb-users/${encodeURIComponent(email)}/reset-password`,
        { password: sifirlaSifre },
        { headers: getAuthHeaders() }
      );
      setSifirlaEmail('');
      setSifirlaSifre(''); // sifre state'te birakilmaz
    });

  const mdb2faSifirla = (email) =>
    mdbIslem(email, () =>
      axios.post(`${API}/admin/mdb-users/${encodeURIComponent(email)}/reset-2fa`, {}, { headers: getAuthHeaders() })
    );

  const mdbSil = (email) =>
    mdbIslem(email, async () => {
      await axios.delete(`${API}/admin/mdb-users/${encodeURIComponent(email)}`, {
        headers: getAuthHeaders(),
      });
      setSilinecek(null);
    });

  const mdbDurumDegistir = (email, yeniDurum) =>
    mdbIslem(email, () =>
      axios.patch(
        `${API}/admin/mdb-users/${encodeURIComponent(email)}`,
        { active: yeniDurum },
        { headers: getAuthHeaders() }
      )
    );

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setAdminEmail('');
    setEmail('');
    setPassword('');
    // 2FA state'leri (Commit 4/4)
    setTempToken('');
    setQrCodeDataUrl('');
    setTotpSecret('');
    setTotpCode('');
    setTotpError('');
    setAuthStep('login');
    setSignupRows([]);
    setColumns([]);
    setInfluencerCount(0);
    setBrandCount(0);
    // Peer admin reset state'leri (Commit 7/7)
    setAdminList([]);
    setPeerResetTarget(null);
    setPeerResetError('');
    setPeerResetSuccess(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePasswordLoading(true);
    setChangePasswordError('');
    setChangePasswordSuccess(false);

    // Frontend hizli validasyon (kullaniciya hizli feedback; backend de aynisini check eder)
    if (newPassword !== confirmNewPassword) {
      setChangePasswordError(t.passwordMismatch);
      setChangePasswordLoading(false);
      return;
    }
    if (newPassword.length < 12) {
      setChangePasswordError(t.passwordTooShort);
      setChangePasswordLoading(false);
      return;
    }
    if (
      !/[A-Z]/.test(newPassword) ||
      !/[a-z]/.test(newPassword) ||
      !/[0-9]/.test(newPassword) ||
      !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword)
    ) {
      setChangePasswordError(t.passwordWeak);
      setChangePasswordLoading(false);
      return;
    }

    try {
      const headers = getAuthHeaders();
      await axios.post(
        `${API}/admin-change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers }
      );

      setChangePasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      // 2 saniye sonra modal'i kapat
      setTimeout(() => {
        setChangePasswordSuccess(false);
        setShowChangePasswordModal(false);
      }, 2000);
    } catch (err) {
      if (err.response?.status === 401) {
        setChangePasswordError(t.currentPasswordWrong);
      } else if (err.response?.data?.error) {
        setChangePasswordError(err.response.data.error);
      } else {
        setChangePasswordError(t.changePasswordError);
      }
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const closeChangePasswordModal = () => {
    setShowChangePasswordModal(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setChangePasswordError('');
    setChangePasswordSuccess(false);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
  };

  // 2FA Setup Screen — ilk girişte zorunlu (Commit 4/4)
  if (!isAuthenticated && authStep === '2fa-setup') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-900 rounded-xl border-2 border-[#FFDD59] p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FFDD59]/20 rounded-full mb-4">
              <ShieldCheck className="text-[#FFDD59]" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{t.totpSetupTitle}</h1>
            <p className="text-gray-400 text-sm">{t.totpSetupSubtitle}</p>
          </div>

          <div className="space-y-4 text-sm text-gray-300 mb-6">
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#FFDD59] text-black rounded-full flex items-center justify-center font-bold text-xs">1</span>
              <p>{t.totpStep1}</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#FFDD59] text-black rounded-full flex items-center justify-center font-bold text-xs">2</span>
              <p>{t.totpStep2}</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#FFDD59] text-black rounded-full flex items-center justify-center font-bold text-xs">3</span>
              <p>{t.totpStep3}</p>
            </div>
          </div>

          {qrCodeDataUrl && (
            <div className="bg-white p-4 rounded-lg mb-4 flex justify-center">
              <img
                src={qrCodeDataUrl}
                alt="2FA QR Code"
                className="w-48 h-48"
                data-testid="totp-qr-code"
              />
            </div>
          )}

          {totpSecret && (
            <details className="mb-4">
              <summary className="text-xs text-gray-400 cursor-pointer hover:text-white">
                {t.totpManualEntry}
              </summary>
              <p className="text-xs text-gray-500 mt-2 break-all font-mono bg-gray-800 p-2 rounded">
                {totpSecret}
              </p>
            </details>
          )}

          <form onSubmit={handle2FAVerify}>
            <label className="block text-sm text-gray-400 mb-2">{t.totpEnterCode}</label>
            <input
              type="text"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              autoFocus
              className="w-full text-center text-2xl tracking-widest py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#FFDD59] font-mono"
              data-testid="totp-code-input"
            />

            {totpError && (
              <div
                className="mt-3 p-3 bg-red-500/20 border border-red-500 text-red-300 rounded-lg text-sm"
                data-testid="totp-error"
              >
                {totpError}
              </div>
            )}

            <button
              type="submit"
              disabled={totpLoading || totpCode.length !== 6}
              className="w-full mt-4 py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-500 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="totp-verify-button"
            >
              {totpLoading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  {t.totpVerifying}
                </>
              ) : (
                t.totpVerifyAndContinue
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // 2FA Verify Screen — totp_enabled=true kullanicilar icin (sonraki girisler)
  if (!isAuthenticated && authStep === '2fa-verify') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-900 rounded-xl border-2 border-[#FFDD59] p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FFDD59]/20 rounded-full mb-4">
              <ShieldCheck className="text-[#FFDD59]" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{t.totpVerifyTitle}</h1>
            <p className="text-gray-400 text-sm">{t.totpVerifySubtitle}</p>
          </div>

          <form onSubmit={handle2FAVerify}>
            <label className="block text-sm text-gray-400 mb-2">{t.totpEnterCode}</label>
            <input
              type="text"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              autoFocus
              className="w-full text-center text-2xl tracking-widest py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#FFDD59] font-mono"
              data-testid="totp-code-input"
            />

            {totpError && (
              <div
                className="mt-3 p-3 bg-red-500/20 border border-red-500 text-red-300 rounded-lg text-sm"
                data-testid="totp-error"
              >
                {totpError}
              </div>
            )}

            <button
              type="submit"
              disabled={totpLoading || totpCode.length !== 6}
              className="w-full mt-4 py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-500 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="totp-verify-button"
            >
              {totpLoading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  {t.totpVerifying}
                </>
              ) : (
                t.totpVerifyButton
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setAuthStep('login');
                setTempToken('');
                setTotpCode('');
                setTotpError('');
              }}
              className="w-full mt-3 py-2 text-gray-400 hover:text-white transition text-sm"
            >
              ← {t.totpBackToLogin}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-md w-full mx-4">
          <div className="bg-gray-900 border-2 border-[#FFDD59] rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <Lock className="mx-auto mb-4" size={56} color="#FFDD59" />
              <h1 className="text-3xl font-bold text-white mb-2">{t.loginTitle}</h1>
              <p className="text-gray-400">Persona Ajans</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              {/* Email Input — backend allowlist'e karsi check eder */}
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.emailPlaceholder}
                  required
                  autoComplete="email"
                  autoFocus
                  className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#FFDD59]"
                  data-testid="admin-email-input"
                />
              </div>

              {/* Password Input */}
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.passwordPlaceholder}
                  required
                  autoComplete="current-password"
                  className="w-full pl-10 pr-12 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#FFDD59]"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {/* Submit Button — loading state ile */}
              <button
                type="submit"
                disabled={loginLoading || !email || !password}
                className="w-full py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-500 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="admin-login-button"
              >
                {loginLoading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    {t.loggingIn}
                  </>
                ) : (
                  t.login
                )}
              </button>

              {/* Error Message — 401/429/500 ortak gosterim */}
              {error && (
                <div
                  className="p-3 bg-red-900/50 border border-red-500 text-red-200 rounded-lg text-sm text-center"
                  data-testid="admin-error-message"
                >
                  {error}
                </div>
              )}

              {/* Sifremi Unuttum linki — Commit 6/6 (sadece login ekraninda; 2FA ekranlarinda yok) */}
              <div className="text-center mt-3">
                <button
                  type="button"
                  onClick={() => setShowForgotPasswordModal(true)}
                  className="text-sm text-gray-400 hover:text-[#FFDD59] transition"
                  data-testid="forgot-password-link"
                >
                  {t.forgotPassword}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Forgot Password Modal — Login Screen'de de render edilir (Commit 6 hot fix) */}
        {/* Eski hal: modal sadece Admin Panel return'unda vardi → buton tiklanir ama modal acilmazdi. */}
        {forgotPasswordModalJsx}
      </div>
    );
  }

  // Admin Panel (after login)
  return (
    <div className="min-h-screen bg-black text-white relative">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10">
        {/* Header */}
        <div className="bg-[#FFDD59] border-b border-yellow-600">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex justify-between items-center">
            <div className="flex flex-col">
              <h1 className="text-2xl font-bold text-black">PERSONA ADMIN</h1>
              {adminEmail && (
                <p className="text-xs text-black/70 mt-1" data-testid="admin-current-email">
                  {adminEmail}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <a
                href="https://personaajans.com"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-black text-[#FFDD59] font-semibold rounded-lg hover:bg-gray-900 transition flex items-center gap-2"
              >
                <Home size={18} />
                {t.home}
              </a>
              <button
                onClick={() => setShowChangePasswordModal(true)}
                className="px-4 py-2 bg-black text-[#FFDD59] font-semibold rounded-lg hover:bg-gray-900 transition flex items-center gap-2"
                data-testid="change-password-button"
              >
                <Key size={18} />
                {t.changePassword}
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-black text-[#FFDD59] font-semibold rounded-lg hover:bg-gray-900 transition"
              >
                {t.logout}
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* SEKMELER — mevcut icerik 'genel' sekmesine tasindi, kendisi degismedi. */}
          <div className="flex gap-2 mb-8 border-b border-gray-800">
            {[
              { k: 'genel', ad: t.tabGeneral },
              { k: 'mdb', ad: t.tabMdb },
            ].map((sk) => (
              <button
                key={sk.k}
                type="button"
                onClick={() => setAktifSekme(sk.k)}
                className={`px-5 py-3 text-sm font-semibold transition border-b-2 -mb-px ${
                  aktifSekme === sk.k
                    ? 'border-[#FFDD59] text-[#FFDD59]'
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
                data-testid={`admin-tab-${sk.k}`}
              >
                {sk.ad}
              </button>
            ))}
          </div>

          {aktifSekme === 'genel' && (
          <>
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t.title}</h2>
            <p className="text-lg text-gray-400">{t.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Influencer Signups — tek sayim (Brand Contacts karti ile ayni stil) */}
            <div className="bg-gradient-to-br from-gray-900 to-black border border-[#FFDD59] rounded-2xl p-8 transform hover:scale-105 transition shadow-2xl">
              <div className="flex items-center gap-4 mb-6">
                <Users size={48} className="text-[#FFDD59]" />
                <div>
                  <h3 className="text-2xl font-bold text-[#FFDD59]">{t.influencers}</h3>
                  <p className="text-gray-400">{t.total}: {influencerCount} {t.entries}</p>
                </div>
              </div>
              <button
                onClick={() => handleDownload('influencers')}
                className="w-full py-4 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition flex items-center justify-center gap-2"
                data-testid="download-influencers"
              >
                <Download size={20} />
                {t.download}
              </button>
            </div>

            {/* Brand Contacts */}
            <div className="bg-gradient-to-br from-gray-900 to-black border border-[#FFDD59] rounded-2xl p-8 transform hover:scale-105 transition shadow-2xl">
              <div className="flex items-center gap-4 mb-6">
                <Building2 size={48} className="text-[#FFDD59]" />
                <div>
                  <h3 className="text-2xl font-bold text-[#FFDD59]">{t.brands}</h3>
                  <p className="text-gray-400">{t.total}: {brandCount} {t.entries}</p>
                </div>
              </div>
              <button
                onClick={() => handleDownload('brands')}
                className="w-full py-4 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition flex items-center justify-center gap-2"
                data-testid="download-brands"
              >
                <Download size={20} />
                {t.download}
              </button>
            </div>
          </div>

          {/* === Diger Admin Yonetimi (Commit 7/7 — peer admin reset) === */}
          {/* Tatil senaryosu (telefon kaybi vb.) icin: bir admin digerinin 2FA'sini sifirlayabilir. */}
          {/* Self-reset blokeli (backend); buton sadece DIGER admin kartinda gorunur. */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 mb-12">
            <div className="flex items-center gap-3 mb-2">
              <Users className="text-[#FFDD59]" size={28} />
              <h2 className="text-2xl font-bold text-[#FFDD59]">{t.adminMgmtTitle}</h2>
            </div>
            <p className="text-sm text-gray-400 mb-6">{t.adminMgmtSubtitle}</p>

            {loadingAdminList ? (
              <div className="text-center text-gray-400 py-6 text-sm">{t.adminListLoading}</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {adminList.map((admin) => {
                  const isSelf = admin.email === adminEmail;
                  return (
                    <div
                      key={admin.email}
                      className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 flex flex-col gap-2"
                      data-testid={`admin-card-${admin.email}`}
                    >
                      <div className="flex items-center gap-2">
                        <User className="text-gray-400 flex-shrink-0" size={18} />
                        <span className="text-white font-medium text-sm break-all">{admin.email}</span>
                        {isSelf && (
                          <span className="text-xs text-gray-500">{t.adminCardYou}</span>
                        )}
                      </div>

                      {/* 2FA durumu */}
                      <div className="flex items-center gap-2 text-xs">
                        {admin.totp_enabled ? (
                          <>
                            <ShieldCheck className="text-green-400 flex-shrink-0" size={14} />
                            <span className="text-green-400">{t.adminCardTwoFactorEnabled}</span>
                          </>
                        ) : (
                          <>
                            <ShieldOff className="text-yellow-400 flex-shrink-0" size={14} />
                            <span className="text-yellow-400">{t.adminCardTwoFactorDisabled}</span>
                          </>
                        )}
                      </div>

                      {/* Son giris */}
                      <p className="text-xs text-gray-500">
                        {t.adminCardLastLogin}:{' '}
                        {admin.last_login
                          ? new Date(admin.last_login).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US')
                          : t.adminCardLastLoginNever}
                      </p>

                      {/* Son 2FA reset (audit) */}
                      {admin.totp_reset_at && (
                        <p className="text-xs text-gray-500">
                          {t.adminCardLastReset}:{' '}
                          {new Date(admin.totp_reset_at).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US')}
                          {admin.totp_reset_by && ` (${admin.totp_reset_by})`}
                        </p>
                      )}

                      {/* "2FA'yi Sifirla" butonu — sadece DIGER admin icin */}
                      {!isSelf && (
                        <button
                          onClick={() => openPeerResetModal(admin.email)}
                          className="mt-2 w-full py-2 bg-red-600/20 border border-red-500/50 text-red-300 hover:bg-red-600/30 rounded-lg transition flex items-center justify-center gap-2 text-sm font-medium"
                          data-testid={`peer-reset-button-${admin.email}`}
                        >
                          <ShieldOff size={16} />
                          {t.peerResetButton}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Yeni format basvurular tablosu — 41 sutun, Excel formatinin birebir aynisi */}
          {/* Filter/sort/search/pagination/detay drawer YOK; istek uzerine duz tablo. */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-[#FFDD59]">
                {language === 'tr' ? 'Yeni Format Başvurular' : 'New Format Signups'}
              </h2>
              <span className="text-gray-400 text-sm">
                {signupRows.length} {language === 'tr' ? 'kayıt' : 'entries'}
              </span>
            </div>

            {loadingTable ? (
              <div className="text-center text-gray-400 py-12">
                {language === 'tr' ? 'Yükleniyor...' : 'Loading...'}
              </div>
            ) : signupRows.length === 0 ? (
              <div className="text-center text-gray-400 py-12">
                {language === 'tr'
                  ? 'Henüz yeni format başvuru yok.'
                  : 'No new format signups yet.'}
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-800 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-800 sticky top-0 z-10">
                    <tr>
                      {columns.map((col, idx) => (
                        <th
                          key={col.key}
                          className={`px-4 py-3 text-left text-xs font-semibold text-[#FFDD59] uppercase tracking-wider whitespace-nowrap ${
                            idx === 0 ? 'sticky left-0 bg-gray-800 z-20' : ''
                          }`}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {signupRows.map((row, rowIdx) => (
                      <tr
                        key={row.id || rowIdx}
                        className="hover:bg-gray-800/50 transition-colors"
                      >
                        {columns.map((col, colIdx) => {
                          const value = row[col.key];
                          const displayValue =
                            value === null || value === undefined || value === ''
                              ? '—'
                              : String(value);
                          return (
                            <td
                              key={col.key}
                              className={`px-4 py-3 text-gray-300 whitespace-nowrap ${
                                colIdx === 0
                                  ? 'sticky left-0 bg-gray-900 hover:bg-gray-800/50 z-10 font-medium text-white'
                                  : ''
                              }`}
                              title={displayValue}
                            >
                              {/* Uzun degerleri kisalt: 50 karakter sonra '...'; tam deger title attribute'unda */}
                              {displayValue.length > 50
                                ? displayValue.substring(0, 50) + '...'
                                : displayValue}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="mt-12 bg-gray-900 border border-gray-700 rounded-lg p-6 text-center">
            <p className="text-gray-300">
              {language === 'tr'
                ? '💡 Excel dosyaları otomatik olarak indirilecektir. Tüm form verileri tarih sırasına göre düzenlenmiştir.'
                : '💡 Excel files will be downloaded automatically. All form data is organized by date.'}
            </p>
          </div>
          </>
          )}

          {aktifSekme === 'mdb' && (
            <div data-testid="admin-mdb-panel">
              <div className="mb-6">
                <h2 className="text-3xl font-bold mb-2">{t.mdbTitle}</h2>
                <p className="text-gray-400">{t.mdbSubtitle}</p>
              </div>

              {/* Kullanici ekle */}
              <form
                onSubmit={mdbEkle}
                className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-6 flex flex-wrap gap-3 items-end"
              >
                <div className="flex-1 min-w-[240px]">
                  <label className="block text-sm text-gray-400 mb-1">{t.mdbColEmail}</label>
                  <input
                    type="email"
                    required
                    value={mdbYeniEmail}
                    onChange={(e) => setMdbYeniEmail(e.target.value)}
                    placeholder={t.mdbEmailPlaceholder}
                    className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white focus:border-[#FFDD59] outline-none"
                    data-testid="mdb-new-email"
                  />
                </div>
                <div className="min-w-[200px]">
                  <label className="block text-sm text-gray-400 mb-1">{t.mdbPassword}</label>
                  {/* Goster/gizle: admin giris ekranindaki desenin AYNISI (Eye/EyeOff,
                      relative kapsayici + saga hizali dugme). Sebep: tekrar alani yok;
                      yanlis yazilan sifre sessizce kaydediliyor, kullanici giremiyor
                      ve sebebi anlasilmiyordu. */}
                  <div className="relative">
                    <input
                      type={mdbYeniSifreGoster ? 'text' : 'password'}
                      required
                      minLength={12}
                      value={mdbYeniSifre}
                      onChange={(e) => setMdbYeniSifre(e.target.value)}
                      placeholder={t.mdbPasswordHint}
                      className="w-full pl-3 pr-10 py-2 bg-black border border-gray-700 rounded-lg text-white focus:border-[#FFDD59] outline-none"
                      data-testid="mdb-new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setMdbYeniSifreGoster(!mdbYeniSifreGoster)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                      data-testid="mdb-new-password-toggle"
                    >
                      {mdbYeniSifreGoster ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.mdbColRole}</label>
                  <select
                    value={mdbYeniRol}
                    onChange={(e) => setMdbYeniRol(e.target.value)}
                    className="px-3 py-2 bg-black border border-gray-700 rounded-lg text-white focus:border-[#FFDD59] outline-none"
                    data-testid="mdb-new-role"
                  >
                    <option value="team">team</option>
                    <option value="admin">admin</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={mdbBusy === '__yeni__'}
                  className="px-5 py-2 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition disabled:opacity-50"
                  data-testid="mdb-add"
                >
                  {mdbBusy === '__yeni__' ? <Loader2 className="animate-spin" size={18} /> : t.mdbAdd}
                </button>
              </form>

              {mdbError && (
                <div className="mb-4 px-4 py-3 bg-red-900/40 border border-red-700 rounded-lg text-red-200 text-sm"
                     data-testid="mdb-error">
                  {mdbError}
                </div>
              )}

              {mdbLoading ? (
                <div className="flex items-center gap-2 text-gray-400 py-8">
                  <Loader2 className="animate-spin" size={18} /> ...
                </div>
              ) : mdbUsers.length === 0 ? (
                <p className="text-gray-500 py-8" data-testid="mdb-empty">{t.mdbEmpty}</p>
              ) : (
                <>
                  {aktifKullanicilar.length === 0 ? (
                    <p className="text-gray-500 py-6" data-testid="mdb-active-empty">{t.mdbEmpty}</p>
                  ) : (
                    mdbTablo(aktifKullanicilar, 'mdb-table')
                  )}

                  {/* ARSIV: ayri bir alan YOK — active === false olan hesap arsivdedir.
                      Ayrim tamamen arayuzde; sunucu tarafi degismedi.
                      Kapali hesap yoksa bolum hic cizilmez. */}
                  {arsivKullanicilar.length > 0 && (
                    <div className="mt-6">
                      <button
                        type="button"
                        onClick={() => setArsivAcik((o) => !o)}
                        className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition"
                        data-testid="mdb-archive-toggle"
                      >
                        <span>{t.mdbArchive} ({arsivKullanicilar.length})</span>
                        <span className="text-xs">{arsivAcik ? '▴' : '▾'}</span>
                      </button>
                      {arsivAcik && (
                        <div className="mt-3" data-testid="mdb-archive-panel">
                          {mdbTablo(arsivKullanicilar, 'mdb-archive-table')}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* === HESAP SILME ONAYI ===
          Tek tiklamayla silme YOK. Metin bilerek ölçülü: audit_log AYRI bir
          koleksiyonda ve bu islemle SILINMIYOR — "denetim kayitlari da silinecek"
          demek sistemin yapmadigi bir seyi soylemek olurdu. Soylenen sey dogru olan:
          hesap listeden kalkinca gecmisi izlemek zorlasir. */}
      {silinecek && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-red-800 rounded-2xl p-6 max-w-lg w-full"
               data-testid="mdb-delete-modal">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle size={22} className="text-red-400 shrink-0 mt-0.5" />
              <h3 className="text-lg font-bold text-white">{t.mdbDeleteTitle}</h3>
            </div>
            <p className="text-[#FFDD59] font-mono text-sm mb-4 break-all" data-testid="mdb-delete-target">
              {silinecek}
            </p>
            <p className="text-gray-300 text-sm leading-relaxed mb-3">{t.mdbDeleteBody}</p>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">{t.mdbDeleteAlt}</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setSilinecek(null)}
                disabled={mdbBusy === silinecek}
                className="flex-1 py-2.5 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition disabled:opacity-50"
                data-testid="mdb-delete-cancel"
              >
                {t.mdbDeleteCancel}
              </button>
              <button
                type="button"
                onClick={() => mdbSil(silinecek)}
                disabled={mdbBusy === silinecek}
                className="flex-1 py-2.5 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="mdb-delete-confirm"
              >
                {mdbBusy === silinecek ? <Loader2 className="animate-spin" size={16} /> : t.mdbDeleteConfirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === Şifre Değiştirme Modal (Commit 3/4) === */}
      {/* fixed inset-0: tum ekrani kaplar; backdrop-blur ile arkaplan yumusatma. */}
      {/* Modal disina (backdrop) tiklayinca kapanir; X butonu da var. */}
      {showChangePasswordModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeChangePasswordModal}
        >
          <div
            className="bg-gray-900 border-2 border-[#FFDD59] rounded-xl shadow-2xl max-w-md w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeChangePasswordModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition"
              aria-label="Kapat"
            >
              <X size={24} />
            </button>

            <div className="flex items-center gap-3 mb-6">
              <Key className="text-[#FFDD59]" size={28} />
              <h2 className="text-2xl font-bold text-white">{t.changePassword}</h2>
            </div>

            {changePasswordSuccess ? (
              <div
                className="p-4 bg-green-500/20 border border-green-500 text-green-300 rounded-lg text-center"
                data-testid="change-password-success"
              >
                <p className="font-semibold">✓ {t.passwordChangedSuccess}</p>
              </div>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-4">
                {/* Mevcut Sifre */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.currentPassword}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder={t.currentPasswordPlaceholder}
                      required
                      autoComplete="current-password"
                      className="w-full pl-10 pr-12 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#FFDD59]"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                {/* Yeni Sifre */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.newPassword}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t.newPasswordPlaceholder}
                      required
                      autoComplete="new-password"
                      className="w-full pl-10 pr-12 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#FFDD59]"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                {/* Yeni Sifre Onay */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.confirmNewPassword}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      placeholder={t.confirmNewPasswordPlaceholder}
                      required
                      autoComplete="new-password"
                      className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#FFDD59]"
                      data-testid="confirm-new-password-input"
                    />
                  </div>
                </div>

                {/* Sifre Kurallari Hatirlatma */}
                <div className="p-3 bg-gray-800/50 rounded-lg text-xs text-gray-400">
                  <p className="font-semibold mb-1">{t.passwordRules}:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>{t.passwordRule1}</li>
                    <li>{t.passwordRule2}</li>
                    <li>{t.passwordRule3}</li>
                    <li>{t.passwordRule4}</li>
                    <li>{t.passwordRule5}</li>
                  </ul>
                </div>

                {/* Hata Mesaji */}
                {changePasswordError && (
                  <div
                    className="p-3 bg-red-500/20 border border-red-500 text-red-300 rounded-lg text-sm"
                    data-testid="change-password-error"
                  >
                    {changePasswordError}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={
                    changePasswordLoading || !currentPassword || !newPassword || !confirmNewPassword
                  }
                  className="w-full py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-500 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="change-password-submit"
                >
                  {changePasswordLoading ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      {t.changingPassword}
                    </>
                  ) : (
                    t.changePasswordSubmit
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* === Sifremi Unuttum Modal — variable'a alindi (yukarida tanimlandi) === */}
      {/* Hem Login Screen hem Admin Panel return'larinda render edilir (Commit 6 hot fix) */}
      {forgotPasswordModalJsx}

      {/* === Peer 2FA Reset Confirmation Modal (Commit 7/7) === */}
      {/* Bir admin digerinin 2FA'sini sifirlamadan once acik onay alinir. */}
      {peerResetTarget && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={!peerResetLoading ? closePeerResetModal : undefined}
        >
          <div
            className="bg-gray-900 border-2 border-red-500/50 rounded-xl shadow-2xl max-w-md w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {!peerResetLoading && !peerResetSuccess && (
              <button
                type="button"
                onClick={closePeerResetModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition"
                aria-label="Kapat"
              >
                <X size={24} />
              </button>
            )}

            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="text-red-400" size={28} />
              <h2 className="text-xl font-bold text-white">{t.peerResetModalTitle}</h2>
            </div>

            {peerResetSuccess ? (
              <div className="p-4 bg-green-500/20 border border-green-500 text-green-300 rounded-lg text-center" data-testid="peer-reset-success">
                <p className="font-semibold">✓ {t.peerResetSuccessMsg}</p>
                <p className="text-xs text-gray-400 mt-2 break-all">{peerResetTarget}</p>
              </div>
            ) : (
              <>
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
                  <p className="text-sm text-red-300 leading-relaxed">
                    {t.peerResetWarning.replace('{email}', peerResetTarget)}
                  </p>
                </div>

                <p className="text-sm text-gray-300 mb-6">{t.peerResetConfirmation}</p>

                {peerResetError && (
                  <div className="p-3 bg-red-500/20 border border-red-500 text-red-300 rounded-lg text-sm mb-4" data-testid="peer-reset-error">
                    {peerResetError}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={closePeerResetModal}
                    disabled={peerResetLoading}
                    className="flex-1 py-2 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition disabled:opacity-50"
                    data-testid="peer-reset-cancel"
                  >
                    {t.peerResetCancelButton}
                  </button>
                  <button
                    type="button"
                    onClick={handlePeerReset}
                    disabled={peerResetLoading}
                    className="flex-1 py-2 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="peer-reset-confirm"
                  >
                    {peerResetLoading ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        {t.peerResetting}
                      </>
                    ) : (
                      t.peerResetConfirmButton
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
